import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

const OMOP_CRITIC_PANEL_COPY = {
  de: {
    title: 'Vorschlag verbessern',
    intro: 'Der Vorschlag passt nicht ganz? Geben Sie Feedback, und die KI schlägt Ihnen neue passende OMOP Concepts vor.',
    history: 'Bisherige Verbesserungen',
    yourFeedback: 'Ihr Feedback:',
    by: 'von',
    result: 'Ergebnis der Anpassung:',
    before: 'Vorher:',
    after: 'Nachher:',
    feedbackPlaceholder: "z.B. 'Der Vorschlag ist zu allgemein, ich suche ein spezifischeres Konzept für...' oder 'Das Konzept passt nicht, weil...'",
    loading: 'KI erstellt neue Vorschläge...',
    submit: 'Neue Vorschläge erhalten',
    processingInfo: 'Die KI analysiert Ihr Feedback und erstellt neue Vorschläge. Dies kann einige Sekunden dauern...',
    noRationale: 'Keine Begründung angegeben.',
    model: 'Modell',
    unknownModel: 'unbekannt',
    unknownTimestamp: 'unbekannt',
    stage: 'Stufe',
  },
  en: {
    title: 'Improve suggestion',
    intro: 'Does the suggestion not quite fit? Give feedback and the AI will propose new matching OMOP concepts.',
    history: 'Previous improvements',
    yourFeedback: 'Your feedback:',
    by: 'by',
    result: 'Adjustment result:',
    before: 'Before:',
    after: 'After:',
    feedbackPlaceholder: "e.g. 'The suggestion is too general, I need a more specific concept for...' or 'The concept does not fit because...'",
    loading: 'AI is creating new suggestions...',
    submit: 'Get new suggestions',
    processingInfo: 'The AI is analyzing your feedback and creating new suggestions. This may take a few seconds...',
    noRationale: 'No rationale provided.',
    model: 'Model',
    unknownModel: 'unknown',
    unknownTimestamp: 'unknown',
    stage: 'Stage',
  },
  pt: {
    title: 'Melhorar sugestão',
    intro: 'A sugestão não se encaixa totalmente? Dê feedback e a IA irá propor novos conceitos OMOP adequados.',
    history: 'Melhorias anteriores',
    yourFeedback: 'O seu feedback:',
    by: 'por',
    result: 'Resultado do ajuste:',
    before: 'Antes:',
    after: 'Depois:',
    feedbackPlaceholder: "ex.: 'A sugestão é demasiado geral, procuro um conceito mais específico para...' ou 'O conceito não serve porque...'",
    loading: 'A IA está a criar novas sugestões...',
    submit: 'Obter novas sugestões',
    processingInfo: 'A IA está a analisar o seu feedback e a criar novas sugestões. Isto pode demorar alguns segundos...',
    noRationale: 'Nenhuma justificação indicada.',
    model: 'Modelo',
    unknownModel: 'desconhecido',
    unknownTimestamp: 'desconhecido',
    stage: 'Etapa',
  },
};

/**
 * Der reale Iterations-Contract (parameters.json) fuehrt die Begruendung des
 * Critic-Agenten als `agentResponse`, nicht als `rationale` auf `newTopMatch`
 * (#100 Rueckmeldung Runde 1).
 */
const resolveIterationRationale = (iteration) =>
  iteration?.newTopMatch?.rationale || iteration?.agentResponse || null;

const formatTimestamp = (value, locale) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString(locale);
};

/**
 * OmopCriticAgentPanel Component
 * Human-in-the-loop Critic Agent interface for iterative mapping refinement
 * Shows history of critic iterations and provides feedback interface
 *
 * @param {object} props
 * @param {array} props.criticHistory - Array of critic agent iterations
 * @param {function} props.onSubmitFeedback - Callback when user submits feedback
 * @param {boolean} props.isProcessing - Whether critic agent is currently processing
 */
const OmopCriticAgentPanel = ({
  criticHistory = [],
  onSubmitFeedback,
  isProcessing = false
}) => {
  const { i18n } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [expandedIteration, setExpandedIteration] = useState(false);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_CRITIC_PANEL_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  const handleSubmit = () => {
    if (feedback.trim() && onSubmitFeedback) {
      onSubmitFeedback(feedback);
      setFeedback('');
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedIteration(isExpanded ? panel : false);
  };

  return (
    <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PsychologyIcon color="primary" />
        <Typography variant="subtitle2" fontWeight="bold">
          {copy.title}
        </Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {copy.intro}
      </Typography>

      {/* Previous Feedback History (collapsed by default) */}
      {criticHistory.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {copy.history} ({criticHistory.length})
          </Typography>
          {criticHistory.map((iteration, idx) => (
            <Accordion
              key={idx}
              expanded={expandedIteration === `iteration-${idx}`}
              onChange={handleAccordionChange(`iteration-${idx}`)}
              sx={{ mb: 1, border: '1px solid #e0e0e0' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Chip label={`#${iteration.iteration}`} size="small" color="primary" variant="outlined" />
                  <Typography variant="body2" noWrap sx={{ flex: 1, overflow: 'hidden' }}>
                    {iteration.feedback.substring(0, 50)}...
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {iteration.user}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {/* User Feedback */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {copy.yourFeedback}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fafafa' }}>
                      <Typography variant="body2">
                        {iteration.feedback}
                      </Typography>
                    </Paper>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                      {copy.by} {iteration.user} • {formatTimestamp(iteration.timestamp, locale) || copy.unknownTimestamp}
                      {' • '}{copy.model}: {iteration.model || copy.unknownModel}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Result: Before/After Comparison */}
                  <Box>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {copy.result}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {/* Previous Top Match */}
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 1.5, backgroundColor: '#fafafa', opacity: 0.7 }}
                      >
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                          {copy.before}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {iteration.previousTopMatch.conceptName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {iteration.previousTopMatch.conceptId}
                        </Typography>
                        <Chip
                          label={`${Math.round(iteration.previousTopMatch.confidenceScore * 100)}%`}
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Paper>

                      {/* New Top Match */}
                      <Paper
                        variant="outlined"
                        sx={{ flex: 1, p: 1.5, backgroundColor: '#e8f5e9', border: '2px solid #4caf50' }}
                      >
                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                          {copy.after}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {iteration.newTopMatch.conceptName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {iteration.newTopMatch.conceptId}
                        </Typography>
                        <Chip
                          label={`${Math.round(iteration.newTopMatch.confidenceScore * 100)}%`}
                          size="small"
                          color="success"
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                      <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#f5f5f5' }}>
                        {iteration.newTopMatch.stage && (
                          <Chip label={`${copy.stage}: ${iteration.newTopMatch.stage}`} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                        )}
                        {(() => {
                          const rationale = resolveIterationRationale(iteration);
                          return (
                            <Typography
                              variant="caption"
                              color={rationale ? 'textSecondary' : 'text.disabled'}
                              display="block"
                              fontStyle={rationale ? 'normal' : 'italic'}
                            >
                              <InfoIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              {rationale || copy.noRationale}
                            </Typography>
                          );
                        })()}
                      </Paper>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Feedback Input */}
      <Box>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          {copy.yourFeedback}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={copy.feedbackPlaceholder}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isProcessing}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={isProcessing ? <CircularProgress size={20} /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={!feedback.trim() || isProcessing}
          fullWidth
        >
          {isProcessing ? copy.loading : copy.submit}
        </Button>
      </Box>

      {isProcessing && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {copy.processingInfo}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default OmopCriticAgentPanel;
